import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../utils/api';
import { toast } from 'sonner';
import { Bell, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="notifications-page" className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Notifications
        </h1>
        <p className="text-slate-600">
          {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="space-y-3">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              data-testid={`notification-${notification.id}`}
              className={`bg-white border border-slate-200 shadow-sm rounded-xl p-6 ${
                !notification.read ? 'border-l-4 border-l-blue-600' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-900 mb-1">{notification.message}</p>
                  <p className="text-sm text-slate-500">
                    {format(new Date(notification.created_at), 'MMM dd, yyyy hh:mm a')}
                  </p>
                  {notification.job_id && (
                    <Link
                      to={`/jobs/${notification.job_id}`}
                      className="text-sm text-blue-600 hover:text-blue-700 mt-2 inline-block"
                    >
                      View Job
                    </Link>
                  )}
                </div>
                {!notification.read && (
                  <button
                    data-testid={`mark-read-${notification.id}`}
                    onClick={() => handleMarkRead(notification.id)}
                    className="text-slate-400 hover:text-blue-600 transition-colors"
                  >
                    <CheckCircle2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-12 text-center">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No notifications yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;